/**
 * Admin Hub Context
 * Provides state management for Admin Hub navigation, favorites, and recents
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ADMIN_HUB_STORAGE_KEYS,
  buildNavigationTree,
  filterByPermission,
  filterNavigationTree,
  findPathToNode,
  getAllNavigablePages,
  type TreeNavItem,
} from '../../../config/adminHubConfig';

// Types
export interface FavoriteItem {
  path: string;
  label: string;
  section: string;
  addedAt: number;
}

export interface RecentItem {
  path: string;
  label: string;
  section: string;
  timestamp: number;
}

export interface AdminHubContextValue {
  // Navigation state
  activeSection: string | null;
  activePath: string | null;
  expandedNodes: string[];
  sidebarCollapsed: boolean;
  filteredNavTree: TreeNavItem[];

  // Filter state
  filterText: string;
  displayNavTree: TreeNavItem[];

  // User preferences
  favorites: FavoriteItem[];
  recentItems: RecentItem[];

  // Quick access collapsed state
  quickAccessCollapsed: boolean;

  // Command palette
  commandPaletteOpen: boolean;

  // Actions
  toggleSidebarCollapse: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleNode: (nodeId: string) => void;
  expandNode: (nodeId: string) => void;
  collapseNode: (nodeId: string) => void;
  setExpandedNodes: (nodes: string[]) => void;
  expandAllNodes: () => void;
  collapseAllNodes: () => void;
  addFavorite: (path: string, label: string, section: string) => void;
  removeFavorite: (path: string) => void;
  isFavorite: (path: string) => boolean;
  addRecentItem: (item: Omit<RecentItem, 'timestamp'>) => void;
  clearRecents: () => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  navigateTo: (path: string) => void;
  setFilterText: (text: string) => void;
  clearFilter: () => void;
  toggleQuickAccessCollapsed: () => void;
}

// Create context with undefined default
const AdminHubContext = createContext<AdminHubContextValue | undefined>(undefined);

// Storage helpers
const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error(`Error loading ${key} from localStorage:`, e);
  }
  return defaultValue;
};

const saveToStorage = <T,>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Error saving ${key} to localStorage:`, e);
  }
};

// Provider props
interface AdminHubProviderProps {
  children: React.ReactNode;
  userPermissions: string[];
}

// Provider component
export const AdminHubProvider: React.FC<AdminHubProviderProps> = ({
  children,
  userPermissions,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Build and filter navigation tree based on permissions
  const filteredNavTree = useMemo(() => {
    const fullTree = buildNavigationTree();
    return filterByPermission(fullTree, userPermissions);
  }, [userPermissions]);

  // All navigable pages (filtered by permission)
  const navigablePages = useMemo(
    () => getAllNavigablePages(filteredNavTree),
    [filteredNavTree]
  );

  // State
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() =>
    loadFromStorage(ADMIN_HUB_STORAGE_KEYS.SIDEBAR_COLLAPSED, false)
  );

  const [expandedNodes, setExpandedNodesState] = useState<string[]>(() =>
    loadFromStorage(ADMIN_HUB_STORAGE_KEYS.EXPANDED_NODES, [])
  );

  const [favorites, setFavorites] = useState<FavoriteItem[]>(() =>
    loadFromStorage(ADMIN_HUB_STORAGE_KEYS.FAVORITES, [])
  );

  const [recentItems, setRecentItems] = useState<RecentItem[]>(() =>
    loadFromStorage(ADMIN_HUB_STORAGE_KEYS.RECENTS, [])
  );

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const [filterText, setFilterTextState] = useState('');

  const [quickAccessCollapsed, setQuickAccessCollapsed] = useState(() =>
    loadFromStorage(ADMIN_HUB_STORAGE_KEYS.QUICK_ACCESS_COLLAPSED, false)
  );

  // Derive active section and path from current location (include search params for matching)
  const fullPath = location.pathname + location.search;
  const activePath = fullPath;
  const activeSection = useMemo(() => {
    // Try exact match first (with query string)
    let page = navigablePages.find(p => p.path === fullPath);
    // Fallback to pathname-only match for backwards compatibility
    if (!page) {
      page = navigablePages.find(p => p.path === location.pathname || p.path.split('?')[0] === location.pathname);
    }
    return page?.section ?? null;
  }, [navigablePages, fullPath, location.pathname]);

  // Persist state to localStorage
  useEffect(() => {
    saveToStorage(ADMIN_HUB_STORAGE_KEYS.SIDEBAR_COLLAPSED, sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    saveToStorage(ADMIN_HUB_STORAGE_KEYS.EXPANDED_NODES, expandedNodes);
  }, [expandedNodes]);

  useEffect(() => {
    saveToStorage(ADMIN_HUB_STORAGE_KEYS.FAVORITES, favorites);
  }, [favorites]);

  useEffect(() => {
    saveToStorage(ADMIN_HUB_STORAGE_KEYS.RECENTS, recentItems);
  }, [recentItems]);

  // Track recent items when navigating within Admin Hub
  useEffect(() => {
    if (location.pathname.startsWith('/admin/hub')) {
      // Try exact match first (with query string)
      let page = navigablePages.find(p => p.path === fullPath);
      // Fallback to pathname-only match
      if (!page) {
        page = navigablePages.find(p => p.path.split('?')[0] === location.pathname);
      }
      if (page) {
        addRecentItem({
          path: page.path,
          label: page.label,
          section: page.section,
        });
      }
    }
  }, [fullPath, location.pathname, navigablePages]);

  // Persist quickAccessCollapsed to localStorage
  useEffect(() => {
    saveToStorage(ADMIN_HUB_STORAGE_KEYS.QUICK_ACCESS_COLLAPSED, quickAccessCollapsed);
  }, [quickAccessCollapsed]);

  // Compute display nav tree based on filter text
  const displayNavTree = useMemo(() => {
    if (!filterText.trim()) {
      return filteredNavTree;
    }
    return filterNavigationTree(filteredNavTree, filterText);
  }, [filteredNavTree, filterText]);

  // Auto-expand to active path on initial mount (Phase 3)
  const hasAutoExpanded = React.useRef(false);
  useEffect(() => {
    // Auto-expand once on initial mount, merging into any persisted expansion state
    // (wait for permissions to load so the tree is populated)
    if (!hasAutoExpanded.current && filteredNavTree.length > 0 && activePath && activePath !== '/admin/hub') {
      hasAutoExpanded.current = true;
      const pathToExpand = findPathToNode(filteredNavTree, activePath);
      if (pathToExpand.length > 0) {
        setExpandedNodesState(prev => Array.from(new Set([...prev, ...pathToExpand])));
      }
    }
  }, [activePath, filteredNavTree]);

  // When filter text changes, auto-expand all matching nodes
  useEffect(() => {
    if (filterText.trim()) {
      // Expand all nodes when filtering to show matches
      const allIds = getAllExpandableNodeIds(displayNavTree);
      setExpandedNodesState(allIds);
    }
  }, [filterText, displayNavTree]);

  // Actions
  const toggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsedState(prev => !prev);
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
  }, []);

  // Get top-level node IDs for accordion behavior
  const topLevelNodeIds = useMemo(() => {
    return filteredNavTree.map(node => node.id);
  }, [filteredNavTree]);

  // Get all expandable child IDs for a top-level node
  const getChildExpandableIds = useCallback((nodeId: string): string[] => {
    const topLevelNode = filteredNavTree.find(n => n.id === nodeId);
    if (!topLevelNode?.children) return [];

    const ids: string[] = [];
    const collectIds = (items: typeof filteredNavTree) => {
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          ids.push(item.id);
          collectIds(item.children);
        }
      }
    };
    collectIds(topLevelNode.children);
    return ids;
  }, [filteredNavTree]);

  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodesState(prev => {
      const isCurrentlyExpanded = prev.includes(nodeId);

      // If collapsing, just remove this node
      if (isCurrentlyExpanded) {
        return prev.filter(id => id !== nodeId);
      }

      // If expanding a top-level node, collapse other top-level nodes (accordion behavior)
      // and expand all subgroups within this top-level node
      const isTopLevel = topLevelNodeIds.includes(nodeId);
      if (isTopLevel) {
        // Get all expandable children of this top-level node
        const childExpandableIds = getChildExpandableIds(nodeId);
        // Remove all other top-level nodes and their children, add this node and all its children
        const otherTopLevelAndChildren = topLevelNodeIds
          .filter(id => id !== nodeId)
          .flatMap(id => [id, ...getChildExpandableIds(id)]);
        return [
          ...prev.filter(id => !otherTopLevelAndChildren.includes(id) && !topLevelNodeIds.includes(id)),
          nodeId,
          ...childExpandableIds,
        ];
      }

      // For nested nodes, just add normally
      return [...prev, nodeId];
    });
  }, [topLevelNodeIds, getChildExpandableIds]);

  const expandNode = useCallback((nodeId: string) => {
    setExpandedNodesState(prev =>
      prev.includes(nodeId) ? prev : [...prev, nodeId]
    );
  }, []);

  const collapseNode = useCallback((nodeId: string) => {
    setExpandedNodesState(prev => prev.filter(id => id !== nodeId));
  }, []);

  const setExpandedNodes = useCallback((nodes: string[]) => {
    setExpandedNodesState(nodes);
  }, []);

  // Get all expandable node IDs from the tree
  const getAllExpandableNodeIds = useCallback((nodes: TreeNavItem[]): string[] => {
    const ids: string[] = [];
    const traverse = (items: TreeNavItem[]) => {
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          ids.push(item.id);
          traverse(item.children);
        }
      }
    };
    traverse(nodes);
    return ids;
  }, []);

  const expandAllNodes = useCallback(() => {
    const allIds = getAllExpandableNodeIds(filteredNavTree);
    setExpandedNodesState(allIds);
  }, [filteredNavTree, getAllExpandableNodeIds]);

  const collapseAllNodes = useCallback(() => {
    setExpandedNodesState([]);
  }, []);

  const addFavorite = useCallback((path: string, label: string, section: string) => {
    setFavorites(prev => {
      // Check if already favorited
      if (prev.some(f => f.path === path)) return prev;
      // Max 8 favorites
      const newFavorites = [
        ...prev,
        { path, label, section, addedAt: Date.now() },
      ].slice(-8);
      return newFavorites;
    });
  }, []);

  const removeFavorite = useCallback((path: string) => {
    setFavorites(prev => prev.filter(f => f.path !== path));
  }, []);

  const isFavorite = useCallback((path: string) => {
    return favorites.some(f => f.path === path);
  }, [favorites]);

  const addRecentItem = useCallback((item: Omit<RecentItem, 'timestamp'>) => {
    setRecentItems(prev => {
      // Remove existing entry for this path
      const filtered = prev.filter(r => r.path !== item.path);
      // Add new entry at the beginning
      const newRecents = [
        { ...item, timestamp: Date.now() },
        ...filtered,
      ].slice(0, 10); // Keep only 10 most recent
      return newRecents;
    });
  }, []);

  const clearRecents = useCallback(() => {
    setRecentItems([]);
  }, []);

  const openCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true);
  }, []);

  const closeCommandPalette = useCallback(() => {
    setCommandPaletteOpen(false);
  }, []);

  const navigateTo = useCallback((path: string) => {
    navigate(path);
    closeCommandPalette();
  }, [navigate, closeCommandPalette]);

  const setFilterText = useCallback((text: string) => {
    setFilterTextState(text);
  }, []);

  const clearFilter = useCallback(() => {
    setFilterTextState('');
  }, []);

  const toggleQuickAccessCollapsed = useCallback(() => {
    setQuickAccessCollapsed(prev => !prev);
  }, []);

  // Context value
  const value: AdminHubContextValue = useMemo(() => ({
    activeSection,
    activePath,
    expandedNodes,
    sidebarCollapsed,
    filteredNavTree,
    filterText,
    displayNavTree,
    favorites,
    recentItems,
    quickAccessCollapsed,
    commandPaletteOpen,
    toggleSidebarCollapse,
    setSidebarCollapsed,
    toggleNode,
    expandNode,
    collapseNode,
    setExpandedNodes,
    expandAllNodes,
    collapseAllNodes,
    addFavorite,
    removeFavorite,
    isFavorite,
    addRecentItem,
    clearRecents,
    openCommandPalette,
    closeCommandPalette,
    navigateTo,
    setFilterText,
    clearFilter,
    toggleQuickAccessCollapsed,
  }), [
    activeSection,
    activePath,
    expandedNodes,
    sidebarCollapsed,
    filteredNavTree,
    filterText,
    displayNavTree,
    favorites,
    recentItems,
    quickAccessCollapsed,
    commandPaletteOpen,
    toggleSidebarCollapse,
    setSidebarCollapsed,
    toggleNode,
    expandNode,
    collapseNode,
    setExpandedNodes,
    expandAllNodes,
    collapseAllNodes,
    addFavorite,
    removeFavorite,
    isFavorite,
    addRecentItem,
    clearRecents,
    openCommandPalette,
    closeCommandPalette,
    navigateTo,
    setFilterText,
    clearFilter,
    toggleQuickAccessCollapsed,
  ]);

  return (
    <AdminHubContext.Provider value={value}>
      {children}
    </AdminHubContext.Provider>
  );
};

// Custom hook to use Admin Hub context
export const useAdminHub = (): AdminHubContextValue => {
  const context = useContext(AdminHubContext);
  if (context === undefined) {
    throw new Error('useAdminHub must be used within an AdminHubProvider');
  }
  return context;
};

// Export context for testing purposes
export { AdminHubContext };
